import { JobResponse, jobResponseSchema } from '@parkchirp/reporting';
import { ErrorEvent, EventSource } from 'eventsource';
import ky from 'ky';
import { useEffect, useState } from 'react';

type UseReportSseArgs = {
	authToken: string;
	baseUrl: string;
	jobId?: string;
};

type UseReportSseResult = {
	job: JobResponse | null;
	error: string | null;
	isConnected: boolean;
};

const parseJob = (event: MessageEvent<string>) => {
	return jobResponseSchema.parse(JSON.parse(event.data));
};

export const useReportSse = ({ authToken, baseUrl, jobId }: UseReportSseArgs): UseReportSseResult => {
	const [job, setJob] = useState<JobResponse | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [isConnected, setIsConnected] = useState(false);

	useEffect(() => {
		if (!jobId) {
			return;
		}

		const events = new EventSource(`${baseUrl}/jobs/${jobId}/events`, {
			fetch: ky.create({
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
				timeout: false,
				retry: 0,
				throwHttpErrors: false,
			}),
		});

		const handleJobEvent = (event: MessageEvent<string>) => {
			try {
				const job = parseJob(event);
				setJob(job);
				return true;
			} catch {
				setError('Received an invalid report status update.');
				setIsConnected(false);
				events.close();
				return false;
			}
		};

		events.addEventListener('open', () => {
			setIsConnected(true);
			setError(null);
		});

		events.addEventListener('job.updated', (event) => {
			handleJobEvent(event);
		});

		events.addEventListener('job.closed', (event) => {
			if (handleJobEvent(event)) {
				setIsConnected(false);
				events.close();
			}
		});

		events.addEventListener('error', (event: ErrorEvent) => {
			setIsConnected(false);
			setError(event.message);
		});

		return () => {
			events.close();
		};
	}, [authToken, baseUrl, jobId]);

	return { job, error, isConnected };
};
